import React from 'react'
import PropTypes from 'prop-types'
import { SimRow, SimCell } from 'SharedComponents'
import { formatDate } from 'Shared/helpers'
import CopyLink from './copy_link'
import LeagueCardHub from './league_card_hub'
import { GGToggle } from '../../../shared/new_components/GGToggle'
import { GGButton } from '../../../shared/new_components/GGButton'
import { GGMenu } from '../../../shared/new_components/GGMenu'

const ACTION_ICONS = {
  view_golfers: 'user-o',
  edit_event: 'pen-o',
  copy_link: 'link-o',
  edit_registration_setup: 'gear-o',
}

const LeagueRowHub = ({
  id,
  name,
  startDate,
  memberSize,
  published,
  isRegistrationOpen,
  golfHubLink,
  editEventLink,
  eventRosterLink,
  handlePublish,
  handleRegistration,
  isRegistrationPresent,
  setupRegistrationLink,
  dimension,
  ...props
}) => {
  // `startDate` is a date-only string ("2026-09-19"). `new Date()` parses that
  // form as UTC midnight, so browsers west of UTC render the previous calendar
  // day. moment, via the shared helper, parses date-only strings as
  // local time, so the date survives every viewer timezone.
  const formattedDate = formatDate(startDate, '', 'MMMM D, YYYY')

  const ACTIONS = [
    {
      key: 'view_golfers',
      label: window.I18n.t('gg_golf_hub.dashboard.actions.view_golfers'),
      url: eventRosterLink,
    },
    {
      key: 'edit_event',
      label: window.I18n.t('gg_golf_hub.dashboard.actions.edit_event'),
      url: editEventLink,
    },
  ]

  if (golfHubLink) {
    ACTIONS.push({
      key: 'copy_link',
      label: window.I18n.t('gg_golf_hub.dashboard.actions.copy_link'),
      url: golfHubLink,
      onClick: (e) => {
        navigator.clipboard.writeText(golfHubLink)
        e.preventDefault()
      },
    })
  }

  if (isRegistrationPresent) {
    ACTIONS.push({
      key: 'edit_registration_setup',
      label: window.I18n.t('gg_golf_hub.dashboard.actions.edit_registration_setup'),
      url: setupRegistrationLink,
    })
  }

  const menuOptions = ACTIONS.map(action => ({
    label: action.label,
    value: action.key,
    icon: ACTION_ICONS[action.key],
    usePro: true,
  }))

  const handleActionSelect = (value) => {
    const action = ACTIONS.find(a => a.key === value)
    if (!action) { return }
    if (action.key === 'copy_link') {
      navigator.clipboard.writeText(golfHubLink)
      return
    }
    if (action.url) {
      window.location.href = action.url
    }
  }

  return ( dimension <= 992 ?
    <LeagueCardHub
      id={ id }
      name={name}
      startDate={formattedDate}
      confirmedGolfers={memberSize}
      menuOptions={menuOptions}
      onMenuSelect={handleActionSelect}
      published={published}
      isRegistrationPresent={isRegistrationPresent}
      isRegistrationOpen={isRegistrationOpen}
      setupRegistrationLink={setupRegistrationLink}
      leagueURL={editEventLink}
      setRegistrationOpen={() => handleRegistration(id, isRegistrationOpen)}
    /> :
    <SimRow { ...props } id={ id }>
      <SimCell>
        <a href={ editEventLink }>{ name }</a>
      </SimCell>
      <SimCell className='orange-text text-align'>
        <a href={eventRosterLink}>{ memberSize }</a>
      </SimCell>
      <SimCell className='grey_color'>{ formattedDate }</SimCell>
      <SimCell>
        <GGToggle
          checked={published}
          onChange={() => handlePublish(id, published)}
          labelOn={window.I18n.t('gg_golf_hub.dashboard.published')}
          labelOff={window.I18n.t('gg_golf_hub.dashboard.unpublished')}/>
      </SimCell>
      <SimCell>
        { published && <CopyLink linkUrl={ golfHubLink } /> }
      </SimCell>
      <SimCell>
        {isRegistrationPresent ? <GGToggle
          checked={published && isRegistrationOpen}
          disabled={!published}
          onChange={() => handleRegistration(id, isRegistrationOpen)}
          labelOn={window.I18n.t('gg_golf_hub.dashboard.open')}
          labelOff={window.I18n.t('gg_golf_hub.dashboard.closed')}/> : <GGButton
            variant='ghost'
            label={window.I18n.t('gg_golf_hub.dashboard.set_up')}
            size='sm'
            onClick={() => { window.location.href = setupRegistrationLink }}
          />}
      </SimCell>
      <SimCell className='action_td'>
        <GGMenu
          options={menuOptions}
          onSelect={handleActionSelect}
          position='left'
          iconSize='md'
        />
      </SimCell>
    </SimRow>
  )
}

LeagueRowHub.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  startDate: PropTypes.string,
  memberSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  published: PropTypes.bool,
  isRegistrationOpen: PropTypes.bool,
  golfHubLink: PropTypes.string,
  editEventLink: PropTypes.string,
  eventRosterLink: PropTypes.string,
  handlePublish: PropTypes.func,
  handleRegistration: PropTypes.func,
  isRegistrationPresent: PropTypes.bool,
  setupRegistrationLink: PropTypes.string,
  dimension: PropTypes.number,
}

LeagueRowHub.defaultProps = {
  dimension: window.innerWidth,
}

export default LeagueRowHub
